/**
 * Quote storage operations for offline quotes
 * Handles saving, retrieving, and managing offline quotes with quota management
 */

import { v4 as uuidv4 } from "uuid";
import {
  addToSyncQueue,
  deletePendingQuote,
  getAllPendingQuotes,
  getPendingQuote,
  getPendingQuotesByStatus,
  savePendingQuote,
  updateQuoteStatus,
} from "./db";
import { withQuotaHandling } from "./quota-handler";
import type { OfflineQuote, SyncQueueItem } from "./types";

/**
 * Create and save a new offline quote
 * Automatically adds to sync queue
 */
export async function createOfflineQuote(quoteData: {
  project_id: number;
  customer_name: string;
  customer_email?: string;
  checklist_template_id?: number;
  quote_data: Record<string, unknown>;
  total_amount: number;
}): Promise<{ success: boolean; quote?: OfflineQuote; error?: string }> {
  try {
    const now = new Date().toISOString();
    const quoteId = uuidv4();

    const offlineQuote: OfflineQuote = {
      id: quoteId,
      project_id: quoteData.project_id,
      customer_name: quoteData.customer_name,
      customer_email: quoteData.customer_email,
      checklist_template_id: quoteData.checklist_template_id,
      quote_data: quoteData.quote_data,
      total_amount: quoteData.total_amount,
      created_at: now,
      updated_at: now,
      sync_status: "pending",
      retry_count: 0,
    };

    await withQuotaHandling(() => savePendingQuote(offlineQuote));

    // Add to sync queue
    const queueItem: SyncQueueItem = {
      id: quoteId,
      type: "quote",
      data: offlineQuote,
      created_at: now,
      retry_count: 0,
    };

    await withQuotaHandling(() => addToSyncQueue(queueItem));

    console.log("[Quote Storage] Offline quote created:", quoteId);
    return { success: true, quote: offlineQuote };
  } catch (error) {
    console.error("[Quote Storage] Error creating offline quote:", error);
    const errorMessage = error instanceof Error ? error.message : "Failed to create offline quote";
    return { success: false, error: errorMessage };
  }
}

/**
 * Get a specific offline quote by ID
 */
export async function getOfflineQuote(
  id: string
): Promise<{ success: boolean; quote?: OfflineQuote; error?: string }> {
  try {
    const quote = await getPendingQuote(id);
    if (!quote) {
      return { success: false, error: "Quote not found" };
    }
    return { success: true, quote };
  } catch (error) {
    console.error("[Quote Storage] Error getting offline quote:", error);
    const errorMessage = error instanceof Error ? error.message : "Failed to get offline quote";
    return { success: false, error: errorMessage };
  }
}

/**
 * Get all offline quotes
 */
export async function getOfflineQuotes(): Promise<{
  success: boolean;
  quotes?: OfflineQuote[];
  error?: string;
}> {
  try {
    const quotes = await getAllPendingQuotes();
    return { success: true, quotes };
  } catch (error) {
    console.error("[Quote Storage] Error getting offline quotes:", error);
    const errorMessage = error instanceof Error ? error.message : "Failed to get offline quotes";
    return { success: false, error: errorMessage };
  }
}

/**
 * Get quotes pending sync (not yet uploaded)
 */
export async function getPendingSyncQuotes(): Promise<OfflineQuote[]> {
  try {
    return await getPendingQuotesByStatus("pending");
  } catch (error) {
    console.error("[Quote Storage] Error getting pending sync quotes:", error);
    return [];
  }
}

/**
 * Get count of quotes pending sync
 */
export async function getPendingSyncCount(): Promise<number> {
  try {
    const quotes = await getPendingSyncQuotes();
    return quotes.length;
  } catch (error) {
    console.error("[Quote Storage] Error getting pending sync count:", error);
    return 0;
  }
}

/**
 * Mark quote as syncing (upload in progress)
 */
export async function markQuoteSyncing(id: string): Promise<void> {
  try {
    await updateQuoteStatus(id, "syncing");
  } catch (error) {
    console.error("[Quote Storage] Error marking quote as syncing:", error);
    throw error;
  }
}

/**
 * Mark quote as synced (successfully uploaded)
 * Removes from pending quotes and sync queue
 */
export async function markQuoteSynced(id: string): Promise<void> {
  try {
    await updateQuoteStatus(id, "synced");
    // Note: Actual deletion happens in sync-manager after confirmation
  } catch (error) {
    console.error("[Quote Storage] Error marking quote as synced:", error);
    throw error;
  }
}

/**
 * Mark quote sync as failed
 * Increments retry count
 */
export async function markQuoteFailed(id: string, error: string): Promise<void> {
  try {
    const quote = await getPendingQuote(id);
    if (!quote) {
      console.error("[Quote Storage] Quote not found for failure marking:", id);
      return;
    }

    quote.retry_count = (quote.retry_count || 0) + 1;
    quote.sync_status = "failed";
    quote.last_error = error;
    quote.updated_at = new Date().toISOString();

    await savePendingQuote(quote);
  } catch (error) {
    console.error("[Quote Storage] Error marking quote as failed:", error);
    throw error;
  }
}

/**
 * Delete an offline quote
 */
export async function deleteOfflineQuote(
  id: string
): Promise<{ success: boolean; error?: string }> {
  try {
    await deletePendingQuote(id);
    console.log("[Quote Storage] Offline quote deleted:", id);
    return { success: true };
  } catch (error) {
    console.error("[Quote Storage] Error deleting offline quote:", error);
    const errorMessage = error instanceof Error ? error.message : "Failed to delete offline quote";
    return { success: false, error: errorMessage };
  }
}

/**
 * Get quote statistics
 */
export async function getQuoteStats(): Promise<{
  total: number;
  pending: number;
  syncing: number;
  synced: number;
  failed: number;
}> {
  try {
    const quotes = await getAllPendingQuotes();

    return {
      total: quotes.length,
      pending: quotes.filter((q) => q.sync_status === "pending").length,
      syncing: quotes.filter((q) => q.sync_status === "syncing").length,
      synced: quotes.filter((q) => q.sync_status === "synced").length,
      failed: quotes.filter((q) => q.sync_status === "failed").length,
    };
  } catch (error) {
    console.error("[Quote Storage] Error getting quote stats:", error);
    return { total: 0, pending: 0, syncing: 0, synced: 0, failed: 0 };
  }
}
